import { fmtInt } from "@/lib/api";
import { parsePedidoNaLoja, fmtDate } from "@/lib/orderUtils";
import Barcode from "@/components/orders/Barcode";

/**
 * Renderiza a Ordem de Produção (OP) para impressão A4.
 * Usado tanto em OrderDetail (impressão individual) quanto em BatchPrint (impressão em lote).
 *
 * O componente NÃO controla display:none — quem chama decide onde/quando mostrar
 * (media print no OrderDetail, sempre visível no BatchPrint).
 */
export default function OrderPrintSheet({ order, notes, imageUrl, productionSummary, totalCups, sheetId }) {
  if (!order) return null;
  const items = order.order_items || [];
  const shop = parsePedidoNaLoja(order);

  return (
    <div className="op-sheet" id={sheetId}>
      <div className="op-header">
        <div>
          <div className="overline" style={{ fontSize: 10 }}>ORDEM DE PRODUÇÃO</div>
          <div style={{ fontFamily: "Cabinet Grotesk", fontWeight: 900, fontSize: 36, letterSpacing: "-0.03em", lineHeight: 1 }}>#{order.bling_number}</div>
          {order.bling_number && (
            <div style={{ marginTop: 8 }}>
              <Barcode value={String(order.bling_number)} height={45} fontSize={11} />
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", fontSize: 16, lineHeight: 1.5 }}>
          <div><span className="overline" style={{ fontSize: 9 }}>CLIENTE</span></div>
          <div style={{ fontSize: 22, fontWeight: 900, lineHeight: 1.15, marginBottom: 6 }}>{order.customer || "—"}</div>
          <div style={{ fontSize: 14, fontFamily: "IBM Plex Mono, monospace" }}><strong style={{ fontSize: 10, letterSpacing: "0.08em" }}>DOC</strong> {order.customer_doc || "—"}</div>
          <div style={{ fontSize: 12, marginTop: 4 }}><strong style={{ fontSize: 10, letterSpacing: "0.08em" }}>EMISSÃO</strong> {new Date().toLocaleDateString("pt-BR")}</div>
        </div>
      </div>

      <div className="op-band">
        <div><span className="overline">TOTAL COPOS</span><div className="op-big">{fmtInt(totalCups || 0)}</div></div>
        <div>
          <span className="overline">DATA LIMITE</span>
          <div className="op-big" style={{fontSize:"14pt"}}>{fmtDate(order.exit_date)}</div>
        </div>
        <div>
          <span className="overline">PEDIDO NA LOJA</span>
          {shop.code ? (
            <div>
              <div style={{fontFamily:"IBM Plex Mono", fontWeight:900, fontSize:"14pt", marginTop:"2px"}}>{shop.code}</div>
              <div style={{marginTop:"2px", display:"flex", alignItems:"center", gap:"6px", flexWrap:"wrap"}}>
                <span style={{fontSize:"8pt", fontWeight:900, textTransform:"uppercase", letterSpacing:"0.08em", padding:"1pt 4pt", background: shop.marketplace === "Shopee" ? "#EE4D2D" : shop.marketplace === "Mercado Livre" ? "#FFE600" : "#000", color: shop.marketplace === "Mercado Livre" ? "#000" : "#fff"}}>
                  {shop.marketplace}
                </span>
                {order.store_name && (
                  <span style={{fontSize:"11pt", fontWeight:900, fontFamily:"IBM Plex Mono"}}>{order.store_name}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="op-big" style={{fontSize:"14pt"}}>—</div>
          )}
        </div>
      </div>

      {/* Global image (fallback quando itens não têm arte própria) */}
      {imageUrl && !items.some((it) => it.image_url) && (
        <div className="op-image-wrap"><img src={imageUrl} alt="arte" className="op-image" /></div>
      )}

      {/* Per-type summary with colors */}
      {(productionSummary || []).length > 0 && (
        <div className="op-prod-summary">
          <div className="op-section-title">RESUMO PARA SEPARAÇÃO · POR TIPO</div>
          <table className="op-table">
            <thead><tr><th>Tipo</th><th>Quantidade Total</th><th>Cores</th></tr></thead>
            <tbody>
              {productionSummary.map((s) => (
                <tr key={s.type}>
                  <td><strong style={{fontSize:"11pt"}}>{s.type}</strong></td>
                  <td style={{fontWeight:700, fontSize:"14pt"}}>{s.total_cups} un</td>
                  <td>
                    {Object.keys(s.by_color).length === 0 ? <em style={{color:"#999"}}>(cores não definidas)</em> :
                      Object.entries(s.by_color).map(([c, q]) => <div key={c}><strong>{c}:</strong> {q}</div>)
                    }
                  </td>
                </tr>
              ))}
              <tr style={{background:"#000", color:"#fff"}}>
                <td style={{textAlign:"right", textTransform:"uppercase", letterSpacing:"0.1em", fontSize:"9pt"}}>TOTAL GERAL</td>
                <td style={{fontWeight:900, fontSize:"16pt"}}>{totalCups} copos</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Per-item detail cards */}
      {items.length > 0 && (
        <div>
          <div className="op-section-title">DETALHE POR ITEM</div>
          <div className={`op-items-grid ${items.length === 1 ? "single" : ""}`}>
            {items.map((it, i) => {
              const artImg = it.image_url || (imageUrl && items.every((x) => !x.image_url) ? imageUrl : null);
              return (
                <div key={it.sku || `pr-${i}`} className="op-item-card">
                  <div className="op-item-header">
                    <div>
                      <div style={{fontFamily:"IBM Plex Mono", fontWeight:900, fontSize:"12pt"}}>{it.sku}</div>
                      <div style={{fontSize:"9pt", color:"#333"}}>{it.name}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div className="overline" style={{fontSize:"7pt"}}>TOTAL</div>
                      <div style={{fontFamily:"IBM Plex Mono", fontWeight:900, fontSize:"14pt"}}>{it.cups}</div>
                    </div>
                  </div>
                  {artImg && (
                    <div className="op-item-image-wrap">
                      <img src={artImg} alt={`arte ${it.sku}`} className="op-item-image" />
                    </div>
                  )}
                  <table className="op-item-table">
                    <tbody>
                      <tr><td className="op-label">Cor do Silk</td><td style={{fontWeight:700}}>{it.silk_color || "—"}</td></tr>
                      <tr><td className="op-label">Tipo</td><td>{it.type}</td></tr>
                      <tr>
                        <td className="op-label">Cores dos Copos</td>
                        <td>
                          {(it.color_breakdown || []).length === 0
                            ? <em style={{color:"#999"}}>(cores não definidas)</em>
                            : it.color_breakdown.map((cb, j) => (
                                <span key={cb.color || `pcb-${j}`} style={{display:"inline-block", marginRight:"8px"}}>
                                  <strong>{cb.color}:</strong> {cb.qty}
                                </span>
                              ))
                          }
                        </td>
                      </tr>
                      {it.production_notes && (
                        <tr><td className="op-label">Obs</td><td style={{fontSize:"9pt"}}>{it.production_notes}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {notes && <div className="op-notes"><strong>OBS GERAIS:</strong> {notes}</div>}
    </div>
  );
}

/**
 * CSS de impressão da OP — importado uma vez no App/BatchPrint/OrderDetail.
 * Compartilhado entre impressão individual e em lote.
 */
export const OrderPrintCss = () => (
  <style>{`
    .op-sheet { padding: 0; }
    /* Preview em tela do lote — espelha o layout impresso (com imagens) */
    .batch-op-page .op-header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 12px; margin-bottom: 10px; }
    .batch-op-page .op-band { display: grid; grid-template-columns: 1fr 1fr 1fr; border: 2px solid #000; margin-bottom: 10px; }
    .batch-op-page .op-band > div { padding: 10px 12px; border-right: 1px solid #000; }
    .batch-op-page .op-band > div:last-child { border-right: 0; }
    .batch-op-page .op-band .op-big { font-family: "IBM Plex Mono", monospace; font-weight: 700; font-size: 22px; margin-top: 2px; }
    .batch-op-page .op-image-wrap { border: 2px solid #000; padding: 10px; text-align: center; margin-bottom: 10px; }
    .batch-op-page .op-image { max-width: 100%; max-height: 300px; object-fit: contain; }
    .batch-op-page .op-items-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
    .batch-op-page .op-items-grid.single { grid-template-columns: 1fr; }
    .batch-op-page .op-item-card { border: 2px solid #000; padding: 10px; }
    .batch-op-page .op-item-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #000; padding-bottom: 6px; margin-bottom: 6px; }
    .batch-op-page .op-item-image-wrap { border: 1px solid #000; padding: 6px; text-align: center; margin-bottom: 6px; background: #fafafa; }
    .batch-op-page .op-item-image { max-width: 100%; max-height: 220px; object-fit: contain; }
    .batch-op-page .op-item-table { width: 100%; font-size: 12px; border-collapse: collapse; }
    .batch-op-page .op-item-table td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
    .batch-op-page .op-item-table td.op-label { width: 110px; color: #555; text-transform: uppercase; font-size: 10px; letter-spacing: 0.08em; font-weight: 700; }
    .batch-op-page .op-prod-summary { margin-bottom: 10px; }
    .batch-op-page .op-section-title { background: #000; color: #fff; padding: 6px 12px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; }
    .batch-op-page .op-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 10px; }
    .batch-op-page .op-table th, .batch-op-page .op-table td { border: 1px solid #000; padding: 6px; text-align: left; vertical-align: top; }
    .batch-op-page .op-table th { background: #f0f0f0; text-transform: uppercase; font-size: 10px; letter-spacing: 0.1em; }
    .batch-op-page .op-notes { border-top: 2px solid #000; padding-top: 10px; font-size: 12px; margin-top: 10px; }
    @media print {
      @page { size: A4 portrait; margin: 8mm; }
      html, body { background: white !important; }
      aside, header, .print\\:hidden { display: none !important; }
      /* Cada wrapper do batch (batch-op-page) força uma OP por folha */
      .batch-op-page { display: block !important; break-after: page !important; page-break-after: always !important; break-inside: avoid !important; page-break-inside: avoid !important; }
      .batch-op-page:last-child { break-after: auto !important; page-break-after: auto !important; }
      .op-sheet { display: block; width: 100%; }
      .op-sheet-batch { break-after: page; page-break-after: always; }
      .op-sheet-batch:last-of-type { break-after: auto; page-break-after: auto; }
      .op-header { display: flex; justify-content: space-between; border-bottom: 2px solid #000; padding-bottom: 4mm; margin-bottom: 3mm; }
      .op-band { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; border: 2px solid #000; margin-bottom: 3mm; }
      .op-band > div { padding: 3mm 4mm; border-right: 1px solid #000; }
      .op-band > div:last-child { border-right: 0; }
      .op-band .op-big { font-family: "IBM Plex Mono", monospace; font-weight: 700; font-size: 18pt; margin-top: 2px; }
      .op-band .overline { font-size: 8pt; }
      .op-image-wrap { border: 2px solid #000; padding: 3mm; text-align: center; margin-bottom: 3mm; display: flex; align-items: center; justify-content: center; }
      .op-image { max-width: 100%; max-height: 100mm; object-fit: contain; }
      .op-items-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3mm; margin-bottom: 3mm; }
      .op-items-grid.single { grid-template-columns: 1fr; }
      .op-item-card { border: 2px solid #000; padding: 3mm; break-inside: avoid; page-break-inside: avoid; }
      .op-item-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #000; padding-bottom: 2mm; margin-bottom: 2mm; }
      .op-item-image-wrap { border: 1px solid #000; padding: 2mm; text-align: center; margin-bottom: 2mm; background: #fafafa; }
      .op-item-image { max-width: 100%; max-height: 60mm; object-fit: contain; }
      .op-item-table { width: 100%; font-size: 9pt; border-collapse: collapse; }
      .op-item-table td { padding: 1.5mm 2mm; border-bottom: 1px solid #eee; vertical-align: top; }
      .op-item-table td.op-label { width: 30mm; color: #555; text-transform: uppercase; font-size: 7.5pt; letter-spacing: 0.08em; font-weight: 700; }
      .op-prod-summary { margin-bottom: 3mm; }
      .op-section-title { background: #000; color: #fff; padding: 2mm 4mm; font-size: 10pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.15em; }
      .op-table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 3mm; }
      .op-table th, .op-table td { border: 1px solid #000; padding: 2mm; text-align: left; vertical-align: top; }
      .op-table th { background: #f0f0f0; text-transform: uppercase; font-size: 8pt; letter-spacing: 0.1em; }
      .op-notes { border-top: 2px solid #000; padding-top: 3mm; font-size: 9pt; margin-top: 3mm; }
    }
  `}</style>
);
